import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import './SettingsDetailPage.css'; // reuse the Settings-detail `.sd-*` chrome
import './SampleFieldConfig.css';

/**
 * Settings → Sample Field Configuration — full-page inline detail (ALPACA
 * `VisualMatrix_DetailView_FieldConfiguration` parity). Header = Sample Matrix
 * (name) + Matrix (dbo.Matrix dropdown); three tabs (Station / Sampling
 * Information / Monitoring Records = FieldClass 1 / 0 / 2), each an
 * available↔selected field picker over dbo.SamplingFieldConfiguration with the
 * editable columns Caption / Freeze / Width / SortOrder / IsMandatory /
 * IsReadonly. Saves through the shared useSaveFlow (§18.6).
 */

interface MatrixOption { oid: string; name: string }
interface FieldOption { fieldId: string; fieldCaption: string }
interface FieldRow {
  fieldId: string;
  fieldCaption: string;
  fieldCustomCaption: string | null;
  freeze: boolean;
  width: number;
  sortOrder: number;
  isMandatory: boolean;
  isReadonly: boolean;
}
interface Tab { fieldClass: number; selected: FieldRow[]; available: FieldOption[] }
interface Detail {
  oid: string;
  visualMatrixName: string | null;
  matrixOid: string | null;
  matrixName: string | null;
  visualMatrixCode: string | null;
  tabs: Tab[];
}

const TAB_LABEL: Record<number, string> = {
  1: 'Station',
  0: 'Sampling Information',
  2: 'Monitoring Records',
};

interface Props {
  id: string | null;
  title?: string;
  group?: string;
  onHome?: () => void;
  onSaved: () => void;
  onClose: () => void;
}

export function SampleFieldConfigDetail({ id, title, group, onHome, onSaved, onClose }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();

  const [curId, setCurId] = useState<string | null>(id);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | undefined>();
  const [matrices, setMatrices] = useState<MatrixOption[]>([]);

  const [name, setName] = useState('');
  const [matrixOid, setMatrixOid] = useState('');
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeClass, setActiveClass] = useState<number>(1);

  const editing = curId != null;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t0 = performance.now();
    Promise.all([
      api.get<{ data?: MatrixOption[] }>('/api/v1/sample-field-config/matrices'),
      curId
        ? api.get<{ data?: Detail }>(`/api/v1/sample-field-config/${curId}`)
        : api.get<{ data?: Detail }>('/api/v1/sample-field-config/template'),
    ])
      .then(([mRes, dRes]) => {
        if (!alive) return;
        setMatrices(mRes.data.data ?? []);
        const d = dRes.data.data;
        if (d) {
          setName(d.visualMatrixName ?? '');
          setMatrixOid(d.matrixOid ?? '');
          setTabs(d.tabs ?? []);
          setActiveClass(d.tabs?.[0]?.fieldClass ?? 1);
        }
        setLoadMs(performance.now() - t0);
      })
      .catch(() => { if (alive) notify.error('Could not load the sample field configuration.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curId]);

  const activeTab = useMemo(() => tabs.find((t) => t.fieldClass === activeClass), [tabs, activeClass]);

  const mutateTab = (fieldClass: number, fn: (t: Tab) => Tab) =>
    setTabs((prev) => prev.map((t) => (t.fieldClass === fieldClass ? fn(t) : t)));

  const addField = (fieldClass: number, opt: FieldOption) =>
    mutateTab(fieldClass, (t) => {
      const nextOrder = t.selected.reduce((m, r) => Math.max(m, r.sortOrder), 0) + 1;
      const row: FieldRow = {
        fieldId: opt.fieldId,
        fieldCaption: opt.fieldCaption,
        fieldCustomCaption: null,
        freeze: false,
        width: 100,
        sortOrder: nextOrder,
        isMandatory: false,
        isReadonly: false,
      };
      return {
        ...t,
        selected: [...t.selected, row],
        available: t.available.filter((a) => a.fieldId !== opt.fieldId),
      };
    });

  const removeField = (fieldClass: number, row: FieldRow) =>
    mutateTab(fieldClass, (t) => ({
      ...t,
      selected: t.selected.filter((r) => r.fieldId !== row.fieldId),
      available: [...t.available, { fieldId: row.fieldId, fieldCaption: row.fieldCaption }]
        .sort((a, b) => a.fieldCaption.localeCompare(b.fieldCaption)),
    }));

  const patchRow = (fieldClass: number, fieldId: string, patch: Partial<FieldRow>) =>
    mutateTab(fieldClass, (t) => ({
      ...t,
      selected: t.selected.map((r) => (r.fieldId === fieldId ? { ...r, ...patch } : r)),
    }));

  const valid = () => {
    if (!name.trim()) { notify.error('Sample Matrix must not be empty.'); return false; }
    if (!matrixOid) { notify.error('Matrix must not be empty.'); return false; }
    return true;
  };

  const buildBody = () => ({
    visualMatrixName: name.trim(),
    matrixOid,
    fields: tabs.flatMap((t) => t.selected.map((s) => ({ fieldClass: t.fieldClass, ...s }))),
  });

  const doSave = async (close: boolean) => {
    if (!valid()) return;
    let newId = curId;
    await runSave({
      formName: 'Sample Field Configuration',
      auditAction: editing ? 'edit' : 'create',
      save: async () => {
        const body = buildBody();
        if (curId) await api.put(`/api/v1/sample-field-config/${curId}`, body);
        else {
          const r = await api.post<{ data?: { id?: string } }>('/api/v1/sample-field-config', body);
          newId = r.data.data?.id ?? null;
        }
      },
      onSaved: () => {
        if (close) { onSaved(); return; }
        // Stay on the record — flip a new record into edit mode and reload.
        if (newId && newId !== curId) setCurId(newId);
        else setCurId((x) => x); // trigger reload via effect dep identity is stable; force refetch:
        setReloadTick((n) => n + 1);
      },
    });
  };

  // A lightweight reload trigger for "Save" (stay) on an already-open record.
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    if (reloadTick === 0 || !curId) return;
    let alive = true;
    api.get<{ data?: Detail }>(`/api/v1/sample-field-config/${curId}`).then((dRes) => {
      if (!alive) return;
      const d = dRes.data.data;
      if (d) {
        setName(d.visualMatrixName ?? '');
        setMatrixOid(d.matrixOid ?? '');
        setTabs(d.tabs ?? []);
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const recordName = editing ? `Sample Field Configuration — ${name || '…'}` : 'New Sample Field Configuration';

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <FormTitle
                pageId="set-samplefieldconfig"
                title={recordName}
                loadMs={loadMs}
                loading={loading}
                auditFormName="Sample Field Configuration"
                auditSourceId={curId ?? undefined}
              />
              <div className="sd-bc">
                {onHome && <><a onClick={onHome}>Home</a><i className="ti ti-chevron-right" /></>}
                {group && <><span>{group}</span><i className="ti ti-chevron-right" /></>}
                {title && <><a onClick={onClose}>{title}</a><i className="ti ti-chevron-right" /></>}
                <span>{recordName}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <button className="ca-btn" onClick={() => void doSave(false)} title="Save"><i className="ti ti-device-floppy" /> Save</button>
              <button className="ca-btn" onClick={() => void doSave(true)} title="Save & Close"><i className="ti ti-check" /> Save &amp; Close</button>
              <button className="ca-btn" onClick={onClose} title="Cancel"><i className="ti ti-x" /> Cancel</button>
            </div>
          </div>

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading">Loading…</div>
            ) : (
              <>
                {/* Header fields */}
                <div className="sfc-header">
                  {/* Sample Matrix + Matrix are read-only here — this screen edits
                      the field configuration of an existing matrix only. */}
                  <label className="sfc-field">
                    <span className="sfc-label">Sample Matrix</span>
                    <input className="sd-input sfc-readonly" value={name} readOnly disabled />
                  </label>
                  <label className="sfc-field">
                    <span className="sfc-label">Matrix</span>
                    <select className="sd-input sfc-readonly" value={matrixOid} disabled>
                      <option value="">—</option>
                      {matrices.map((m) => <option key={m.oid} value={m.oid}>{m.name}</option>)}
                    </select>
                  </label>
                </div>

                {/* Tabs */}
                <div className="sfc-tabs">
                  {tabs.map((t) => (
                    <button
                      key={t.fieldClass}
                      className={`sfc-tab${t.fieldClass === activeClass ? ' active' : ''}`}
                      onClick={() => setActiveClass(t.fieldClass)}
                    >
                      {TAB_LABEL[t.fieldClass] ?? `Tab ${t.fieldClass}`}
                      <span className="sfc-tab-count">{t.selected.length}</span>
                    </button>
                  ))}
                </div>

                {activeTab && (
                  <div className="sfc-panels">
                    {/* Available */}
                    <div className="sfc-panel">
                      <div className="sfc-panel-head">Available</div>
                      <div className="sfc-avail">
                        {activeTab.available.length === 0 && <div className="sfc-empty">No more fields</div>}
                        {activeTab.available.map((a) => (
                          <div key={a.fieldId} className="sfc-avail-row">
                            <span className="sfc-avail-name">{a.fieldCaption}</span>
                            <button className="sfc-add" title="Add" onClick={() => addField(activeTab.fieldClass, a)}>
                              <i className="ti ti-circle-plus" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selected */}
                    <div className="sfc-panel sfc-panel-selected">
                      <div className="sfc-panel-head">Selected</div>
                      <div className="sfc-sel-scroll">
                        <table className="sfc-sel-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Name</th>
                              <th>Caption</th>
                              <th>Freeze</th>
                              <th>Width</th>
                              <th>SortOrder</th>
                              <th>IsMandatory</th>
                              <th>IsReadonly</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeTab.selected.length === 0 && (
                              <tr><td colSpan={8} className="sfc-empty">No fields selected — add from the left.</td></tr>
                            )}
                            {activeTab.selected.map((r) => (
                              <tr key={r.fieldId}>
                                <td>
                                  <button className="sfc-remove" title="Remove" onClick={() => removeField(activeTab.fieldClass, r)}>
                                    <i className="ti ti-circle-minus" />
                                  </button>
                                </td>
                                <td className="sfc-name-cell">{r.fieldCaption}</td>
                                <td>
                                  <input className="sfc-cell-input" value={r.fieldCustomCaption ?? ''}
                                    onChange={(e) => patchRow(activeTab.fieldClass, r.fieldId, { fieldCustomCaption: e.target.value })} />
                                </td>
                                <td className="sfc-cell-center">
                                  <input type="checkbox" checked={r.freeze}
                                    onChange={(e) => patchRow(activeTab.fieldClass, r.fieldId, { freeze: e.target.checked })} />
                                </td>
                                <td>
                                  <input type="number" className="sfc-cell-num" value={r.width}
                                    onChange={(e) => patchRow(activeTab.fieldClass, r.fieldId, { width: Number(e.target.value) || 0 })} />
                                </td>
                                <td>
                                  <input type="number" className="sfc-cell-num" value={r.sortOrder}
                                    onChange={(e) => patchRow(activeTab.fieldClass, r.fieldId, { sortOrder: Number(e.target.value) || 0 })} />
                                </td>
                                <td className="sfc-cell-center">
                                  <input type="checkbox" checked={r.isMandatory}
                                    onChange={(e) => patchRow(activeTab.fieldClass, r.fieldId, { isMandatory: e.target.checked })} />
                                </td>
                                <td className="sfc-cell-center">
                                  <input type="checkbox" checked={r.isReadonly}
                                    onChange={(e) => patchRow(activeTab.fieldClass, r.fieldId, { isReadonly: e.target.checked })} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
